import { useCallback, useEffect, useReducer, useRef } from "react";
import workPolicyApi, {
  WorkPolicyApi,
  WorkPolicyDetail
} from "../../../../api/work-policy-api";
import workingTemplatePolicyApi, {
  WorkingTemplatePolicyApi,
  WorkingTemplatePolicyBinding
} from "../../../../api/working-template-policy-api";
import { workPolicyDetailToForm } from "../../../work-type-management/hooks/use-work-policy-form";
import {
  BasePolicyClientError,
  buildBasePolicyOptions,
  createWorkingTemplateBasePolicyState,
  workingTemplateBasePolicyReducer
} from "./working-template-base-policy.model";

export interface UseWorkingTemplateBasePolicyOptions {
  enabled: boolean;
  mode: "create" | "edit";
  workType: number;
  workingTemplateIdx?: number;
  policyApi?: Pick<WorkPolicyApi, "list" | "detail">;
  bindingApi?: Pick<WorkingTemplatePolicyApi, "getBasePolicyBinding">;
}

function clientError(error: unknown): BasePolicyClientError {
  if (error && typeof error === "object") {
    const source = error as Record<string, unknown>;
    return {
      code:
        typeof source.code === "string"
          ? source.code
          : "WORKING_TEMPLATE_POLICY_FAILED",
      message:
        typeof source.message === "string"
          ? source.message
          : "근무 정책 정보를 불러오지 못했습니다.",
      retryable: source.retryable !== false
    };
  }
  return {
    code: "WORKING_TEMPLATE_POLICY_FAILED",
    message: "근무 정책 정보를 불러오지 못했습니다.",
    retryable: true
  };
}

function validDetail(
  detail: WorkPolicyDetail,
  selectedPolicyId: number
): boolean {
  return (
    detail.workPolicyId === selectedPolicyId &&
    detail.policyKind === "BASE" &&
    detail.status === "ACTIVE"
  );
}

export function useWorkingTemplateBasePolicy({
  enabled,
  mode,
  workType,
  workingTemplateIdx,
  policyApi = workPolicyApi,
  bindingApi = workingTemplatePolicyApi
}: UseWorkingTemplateBasePolicyOptions) {
  const [state, dispatch] = useReducer(
    workingTemplateBasePolicyReducer,
    undefined,
    createWorkingTemplateBasePolicyState
  );
  const activationRequest = useRef(0);
  const detailRequest = useRef(0);
  const initializedKey = useRef<string | null>(null);

  const load = useCallback(
    async (forceBinding = false) => {
      if (!enabled || workType !== 10) return;
      const request = ++activationRequest.current;
      const key = `${mode}:${mode === "edit" ? (workingTemplateIdx ?? "missing") : "new"}`;
      const shouldInitialize = initializedKey.current !== key;
      if (shouldInitialize) {
        initializedKey.current = key;
        if (mode === "edit" && workingTemplateIdx) {
          dispatch({ type: "EDIT_STARTED", workingTemplateIdx });
        } else {
          dispatch({ type: "CREATE_STARTED" });
        }
      }
      dispatch({ type: "CATALOG_STARTED" });

      try {
        const catalog = await policyApi.list({ policyKind: "BASE" });
        if (activationRequest.current !== request) return;
        dispatch({
          type: "CATALOG_LOADED",
          result: buildBasePolicyOptions(catalog.data)
        });
      } catch (error) {
        if (activationRequest.current !== request) return;
        dispatch({ type: "CATALOG_FAILED", error: clientError(error) });
        return;
      }

      if (
        mode === "edit" &&
        workingTemplateIdx &&
        (shouldInitialize || forceBinding)
      ) {
        try {
          const binding =
            await bindingApi.getBasePolicyBinding(workingTemplateIdx);
          if (activationRequest.current !== request) return;
          if (binding.data.workType !== workType) {
            dispatch({
              type: "BINDING_FAILED",
              error: {
                code: "WORKING_TEMPLATE_POLICY_TYPE_MISMATCH",
                message: "근무 유형과 기본 정책 연결 정보가 일치하지 않습니다.",
                retryable: true
              }
            });
            return;
          }
          dispatch({ type: "BINDING_LOADED", binding: binding.data });
        } catch (error) {
          if (activationRequest.current !== request) return;
          dispatch({ type: "BINDING_FAILED", error: clientError(error) });
        }
      }
    },
    [bindingApi, enabled, mode, policyApi, workType, workingTemplateIdx]
  );

  useEffect(() => {
    void load(false);
    return () => {
      activationRequest.current += 1;
    };
  }, [load]);

  const loadDetail = useCallback(
    async (policyId: number) => {
      const request = ++detailRequest.current;
      dispatch({ type: "DETAIL_STARTED", policyId, request });
      try {
        const response = await policyApi.detail(policyId);
        if (!validDetail(response.data, policyId)) {
          throw {
            code: "WORKING_TEMPLATE_POLICY_DETAIL_MISMATCH",
            message: "선택한 기본 정책의 상세 정보가 일치하지 않습니다.",
            retryable: true
          };
        }
        dispatch({
          type: "DETAIL_LOADED",
          policyId,
          request,
          detail: response.data,
          snapshot: workPolicyDetailToForm(response.data)
        });
      } catch (error) {
        dispatch({
          type: "DETAIL_FAILED",
          policyId,
          request,
          error: clientError(error)
        });
      }
    },
    [policyApi]
  );

  useEffect(() => {
    if (!enabled || workType !== 10 || state.selectedPolicyId === null) {
      detailRequest.current += 1;
      return;
    }
    void loadDetail(state.selectedPolicyId);
  }, [enabled, loadDetail, state.selectedPolicyId, workType]);

  const selectPolicy = useCallback((policyId: number | null) => {
    detailRequest.current += 1;
    dispatch({ type: "SELECTED", policyId });
  }, []);

  return {
    state,
    selectPolicy,
    retry: () => load(true),
    retryDetail: () =>
      state.selectedPolicyId === null
        ? Promise.resolve()
        : loadDetail(state.selectedPolicyId),
    markBasicSaved: (templateIdx: number) =>
      dispatch({ type: "BASIC_SAVED", workingTemplateIdx: templateIdx }),
    markBindingSaved: (binding: WorkingTemplatePolicyBinding) =>
      dispatch({ type: "BINDING_SAVED", binding }),
    markBindingFailed: (error: unknown) =>
      dispatch({ type: "BINDING_SAVE_FAILED", error: clientError(error) })
  };
}

export type WorkingTemplateBasePolicyController = ReturnType<
  typeof useWorkingTemplateBasePolicy
>;
